import React from 'react';
import styled from 'styled-components';
import useTranslations from '../hooks/useTranslations';

const LanguageSelectorContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const LanguageButton = styled.button`
  background: transparent;
  border: 2px solid #4CAF50;
  color: #4CAF50;
  padding: 8px 16px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: #4CAF50;
    color: white;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3);
  }
`;

const LanguageDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 150px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-10px'});
  transition: all 0.3s ease;
`;

const LanguageOption = styled.button<{ $isActive: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: ${props => props.$isActive ? '#f5f5f5' : 'transparent'};
  color: ${props => props.$isActive ? '#4CAF50' : '#333'};
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #4CAF50;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:only-child {
    border-radius: 8px;
  }
`;

const FlagIcon = styled.span`
  font-size: 16px;
  margin-right: 8px;
`;

interface LanguageSelectorProps {
  className?: string;
}

const getLanguageFlag = (language: string): string => {
  const flags: { [key: string]: string } = {
    it: '🇮🇹',
    en: '🇬🇧',
    pt: '🇧🇷',
    es: '🇪🇸',
    de: '🇩🇪'
  };
  return flags[language] || '🌐';
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className }) => {
  const { currentLanguage, changeLanguage, languages, supportedLanguages } = useTranslations();
  const [isOpen, setIsOpen] = React.useState(false);

  const handleLanguageChange = (language: string) => {
    changeLanguage(language);
    setIsOpen(false);
  };

  const handleClickOutside = React.useCallback((event: MouseEvent) => {
    const target = event.target as Element;
    if (!target.closest('[data-language-selector]')) {
      setIsOpen(false);
    }
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  return (
    <LanguageSelectorContainer className={className} data-language-selector>
      <LanguageButton
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Seleziona lingua"
        aria-expanded={isOpen}
      >
        <FlagIcon>{getLanguageFlag(currentLanguage)}</FlagIcon>
        {languages[currentLanguage] || currentLanguage.toUpperCase()}
        <span style={{ marginLeft: '4px' }}>{isOpen ? '▲' : '▼'}</span>
      </LanguageButton>

      <LanguageDropdown $isOpen={isOpen}>
        {supportedLanguages.map((language) => (
          <LanguageOption
            key={language}
            onClick={() => handleLanguageChange(language)}
            $isActive={language === currentLanguage}
          >
            <FlagIcon>{getLanguageFlag(language)}</FlagIcon>
            {languages[language] || language.toUpperCase()}
          </LanguageOption>
        ))}
      </LanguageDropdown>
    </LanguageSelectorContainer>
  );
};

export default LanguageSelector;
